import React, { useContext, useState, useEffect } from 'react';
import { Search, AlertCircle, Loader2, Eye, Plus, ChevronDown, Filter, X, PenSquare, MoreVertical, Calendar, History, RefreshCw, CreditCard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import superAdminServices from '../../../services/superAdminServices';
import Pagination from '../../../components/Pagination';

export default function Organizations() {
  const navigate = useNavigate();
  const { logoutUser, hasAnyRole, isAuthenticated, user, authTokens } = useContext(AuthContext);

  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null); // Track which dropdown is open
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 10,
    total_pages: 0
  });

  const [filters, setFilters] = useState({
    searchTerm: '',
    subscriptionStatus: '',
    sortBy: 'name'
  });

  // Fetch organizations on mount and when pagination changes
  useEffect(() => {
    fetchOrganizations();
  }, [pagination.page, pagination.page_size]);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      if (filters.searchTerm) {
        params.search = filters.searchTerm;
      }

      if (filters.subscriptionStatus) {
        params.is_subscribed = filters.subscriptionStatus === 'subscribed' ? 'true' : 'false';
      }

      // Add ordering parameter based on sortBy filter
      if (filters.sortBy) {
        switch (filters.sortBy) {
          case 'name':
            params.ordering = 'organisation_name';
            break;
          case 'created_date':
            params.ordering = '-created_at';
            break;
          case 'subscription_date':
            params.ordering = '-subscription_date';
            break;
          default:
            params.ordering = '-created_at';
        }
      }

      const response = await superAdminServices.getAllOrganizations(
        authTokens?.access,
        params
      );

      setOrganizations(response.results || []);
      setPagination(prev => ({
        ...prev,
        count: response.count,
        total_pages: response.total_pages
      }));
    } catch (err) {
      setError(err.message || 'Une erreur est survenue lors du chargement des organisations');
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    // Reset to page 1 when applying new filters
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchOrganizations();
  };

  const handleResetFilters = () => {
    setFilters({
      searchTerm: '',
      subscriptionStatus: '',
      sortBy: 'name'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    // Fetch organizations without filters
    setTimeout(() => fetchOrganizations(), 100);
  };

  const handleViewDetails = (orgId) => {
    navigate(`/admin-panel/organizations/${orgId}`);
  };

  const handleAddOrganization = () => {
    navigate('/admin-panel/organizations/add');
  };

  const navigateToUpdate = (userId) => {
    navigate(`/admin-panel/organizations/${userId}/update`);
  };

  const handleShowFilters = () => {
    setShowFiltersSection(!showFiltersSection);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 // Reset to first page when changing page size
    }));
  };

  // Subscription action handlers
  const handleAddSubscription = (orgId) => {
    navigate(`/admin-panel/subscriptions/add?organization=${orgId}`);
    setOpenDropdown(null);
  };

  const handleUpdateSubscription = (orgId, subscriptionId) => {
    navigate(`/admin-panel/subscriptions/${subscriptionId}/update`);
    setOpenDropdown(null);
  };

  const handleViewSubscriptionDetails = (orgId, subscriptionId) => {
    navigate(`/admin-panel/subscriptions/${subscriptionId}`);
    setOpenDropdown(null);
  };

  const handleViewSubscriptionHistory = (orgId) => {
    navigate(`/admin-panel/organizations/${orgId}/subscription-history`);
    setOpenDropdown(null);
  };

  const toggleDropdown = (orgId) => {
    setOpenDropdown(openDropdown === orgId ? null : orgId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };

    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  // Organizations are now sorted server-side, no need for client-side sorting

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header with add button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Organisations</h2>
          <p className="text-xs text-gray-500 mt-1">Gérez toutes les organisations du système</p>
        </div>

        <div className='flex flex-wrap gap-2'>
          <button
            onClick={handleShowFilters}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>Filtres</span>
          </button>

          <button
            onClick={handleAddOrganization}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter une organisation</span>
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
          <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-red-800">Erreur</h3>
            <p className="text-xs text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className='flex items-center justify-between'>
            <div className="flex items-center mb-4">
              <Filter className="h-5 w-5 text-gray-500 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900">Filtres</h3>
            </div>
            <button onClick={handleShowFilters} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            {/* Search filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Rechercher
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Nom d'organisation..."
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
                  className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Subscription status filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Statut d'abonnement
              </label>
              <select
                value={filters.subscriptionStatus}
                onChange={(e) => handleFilterChange('subscriptionStatus', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="">Tous les statuts</option>
                <option value="subscribed">Abonné</option>
                <option value="not_subscribed">Non abonné</option>
              </select>
            </div>

            {/* Sort by filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">
                Trier par
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="name">Nom</option>
                <option value="created_date">Date de création</option>
                <option value="subscription_date">Date d'abonnement</option>
              </select>
            </div>
          </div>

          {/* Filter action buttons */}
          <div className="flex items-center space-x-3 justify-end">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Réinitialiser
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      )}

      {/* Organizations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Date d'abonnement</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Créé par</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                      <span className="ml-2 text-sm text-gray-600">Chargement...</span>
                    </div>
                  </td>
                </tr>
              ) : organizations.length > 0 ? (
                organizations.map((org) => (
                  <tr
                    key={org.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-xs font-medium text-gray-900">
                      {org.organisation_name}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate">
                      {org.organisation_description || '-'}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {org.subscription_info ? (
                        <div className="flex flex-col space-y-1">
                          <span
                            className={`inline-block px-2 py-1 rounded text-xs font-medium w-fit ${
                              org.subscription_info.status === 'active'
                                ? 'bg-green-100 text-green-700'
                                : org.subscription_info.status === 'expired'
                                ? 'bg-red-100 text-red-700'
                                : org.subscription_info.status === 'suspended'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {org.subscription_info.status === 'active' ? 'Actif' :
                             org.subscription_info.status === 'expired' ? 'Expiré' :
                             org.subscription_info.status === 'suspended' ? 'Suspendu' :
                             org.subscription_info.status === 'cancelled' ? 'Annulé' : 'Aucun'}
                          </span>
                          {org.subscription_info.days_remaining !== null && org.subscription_info.status === 'active' && (
                            <span className="text-xs text-gray-500">
                              {org.subscription_info.days_remaining} jours restants
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          Aucun abonnement
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {org.subscription_info?.start_date
                        ? new Date(org.subscription_info.start_date).toLocaleDateString('fr-FR')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {org.created_by}
                    </td>
                   
                    <td className="px-4 py-3 text-center relative">
                      <div className="flex items-center justify-center space-x-1">
                        {/* View Details Button */}
                        <button
                          onClick={() => handleViewDetails(org.id)}
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        {/* Edit Organization Button */}
                        <button
                          onClick={() => navigateToUpdate(org.id)}
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Modifier l'organisation"
                        >
                          <PenSquare className="h-4 w-4" />
                        </button>

                        {/* More Actions Dropdown */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(org.id);
                            }}
                            className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors"
                            title="Plus d'actions"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>

                          {/* Dropdown Menu */}
                          {openDropdown === org.id && (
                            <div className="absolute right-0 top-8 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                              <div className="py-1">
                                {/* Subscription Actions */}
                                <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
                                  Gestion des abonnements
                                </div>
                                
                                {org.subscription_info?.can_create && (
                                  <button
                                    onClick={() => handleAddSubscription(org.id)}
                                    className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                  >
                                    <Plus className="h-4 w-4 text-green-600" />
                                    <span>Créer un abonnement</span>
                                  </button>
                                )}

                                {org.subscription_info?.can_update && (
                                  <button
                                    onClick={() => handleUpdateSubscription(org.id, org.subscription_info.subscription_id)}
                                    className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                  >
                                    <RefreshCw className="h-4 w-4 text-blue-600" />
                                    <span>
                                      {org.subscription_info.needs_renewal ? 'Renouveler l\'abonnement' : 'Modifier l\'abonnement'}
                                    </span>
                                  </button>
                                )}


                                <button
                                  onClick={() => handleViewSubscriptionHistory(org.id)}
                                  className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                                >
                                  <History className="h-4 w-4 text-orange-600" />
                                  <span>Historique des abonnements</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <p className="text-xs text-gray-500">Aucune organisation trouvée</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="organisations"
        />
      </div>
    </div>
  );
}